import { useState } from 'react';
import { toast } from '../components/Toast';
import { crmApi } from '../services/crmApi';
import { CertificateFormData } from '../components/CertificateFormModal';
import { downloadBlob } from '../utils/downloadBlob';
import type { CrmApplication, Certificate, CertificatePackage } from '../types/crm';

interface DocumentDeps {
  id: string | undefined;
  application: CrmApplication | null;
  setSaving: (v: boolean) => void;
  loadApplication: () => Promise<void>;
}

export function useApplicationDocuments({ id, application, setSaving, loadApplication }: DocumentDeps) {
  const [generatingPUPApplication, setGeneratingPUPApplication] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [documentsRefreshTrigger, setDocumentsRefreshTrigger] = useState(0);

  // Certificates archive
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [showCertificateArchive, setShowCertificateArchive] = useState(false);
  const [loadingCertificates, setLoadingCertificates] = useState(false);
  const [certificatePackage, setCertificatePackage] = useState<CertificatePackage | null>(null);
  const [showCertificates, setShowCertificates] = useState(false);
  const [showCertificateForm, setShowCertificateForm] = useState(false);
  const [generatingCertificates, setGeneratingCertificates] = useState(false);

  const handleDownloadContract = async () => {
    if (!id || !application) return;
    try {
      const blob = await crmApi.getContract(parseInt(id));
      downloadBlob(blob, `Umowa_${application.first_name}_${application.last_name}.txt`);
    } catch (error) {
      console.error('Error downloading contract:', error);
      toast.error('Błąd podczas pobierania umowy');
    }
  };

  const handleDownloadDocuments = async () => {
    if (!id || !application) return;
    try {
      const blob = await crmApi.getDocuments(parseInt(id));
      downloadBlob(blob, `Lista_Dokumentow_${application.first_name}_${application.last_name}.txt`);
    } catch (error) {
      console.error('Error downloading documents:', error);
      toast.error('Błąd podczas pobierania listy dokumentów');
    }
  };

  const handleCalculatePoints = async () => {
    if (!id || !application) return;
    try {
      const data = await crmApi.calculatePoints(parseInt(id), {
        business_type: application.industry,
        is_location_in_krakow: application.is_location_in_krakow || false,
      });
      toast.info(`Punkty obliczone: ${data.points}/${data.maxTotal || 36}\n${data.qualified ? '✓ Kwalifikuje się' : '✗ Za mało punktów (min. ' + (data.requiredMinimum || 22) + ')'}`);
      await loadApplication();
    } catch (error) {
      console.error('Error calculating points:', error);
      toast.error('Błąd podczas obliczania punktów');
    }
  };

  const handleGeneratePUPApplication = async () => {
    if (!id || !application) return;
    setGeneratingPUPApplication(true);
    try {
      const blob = await crmApi.generatePUPApplication(parseInt(id));
      downloadBlob(blob, `Wniosek_PUP_${application.first_name}_${application.last_name}_${application.submission_id}.txt`);
      toast.success('Wniosek PUP wygenerowany pomyślnie!');
    } catch (error) {
      console.error('Error generating PUP application:', error);
      toast.error('Błąd podczas generowania wniosku PUP');
    } finally {
      setGeneratingPUPApplication(false);
    }
  };

  const handleGeneratePUPApplicationPDF = async () => {
    if (!id || !application) return;
    setGeneratingPDF(true);
    try {
      const blob = await crmApi.generatePUPApplicationPDF(parseInt(id));
      downloadBlob(blob, `Wniosek_PUP_${application.first_name}_${application.last_name}_${application.submission_id}.pdf`);
      toast.success('Wniosek PUP (PDF) wygenerowany pomyślnie!');
    } catch (error) {
      console.error('Error generating PUP application PDF:', error);
      toast.error('Błąd podczas generowania wniosku PUP (PDF)');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleSendWelcomeEmail = async () => {
    if (!id || !application) return;
    if (!confirm(`Wysłać email powitalny do ${application.first_name} ${application.last_name}?`)) return;
    setSendingEmail(true);
    try {
      const result = await crmApi.sendWelcomeEmail(parseInt(id));
      if (result.success) {
        toast.success('Email powitalny wysłany pomyślnie!');
        if (result.previewUrl) window.open(result.previewUrl, '_blank');
      } else {
        toast.error('Błąd: ' + result.error);
      }
    } catch (error) {
      console.error('Error sending welcome email:', error);
      toast.error('Błąd podczas wysyłania emaila');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendDocumentPackage = async () => {
    if (!id || !application) return;
    if (!confirm(`Wysłać paczkę dokumentów do ${application.first_name} ${application.last_name}?`)) return;
    setSendingEmail(true);
    try {
      const result = await crmApi.sendDocumentPackage(parseInt(id), []);
      if (result.success) {
        toast.success('Paczka dokumentów wysłana pomyślnie!');
        if (result.previewUrl) window.open(result.previewUrl, '_blank');
      } else {
        toast.error('Błąd: ' + result.error);
      }
    } catch (error) {
      console.error('Error sending document package:', error);
      toast.error('Błąd podczas wysyłania paczki dokumentów');
    } finally {
      setSendingEmail(false);
    }
  };

  const loadCertificates = async () => {
    if (!id) return;
    setLoadingCertificates(true);
    try {
      const token = localStorage.getItem('grant_crm_token');
      const response = await fetch(`/api/crm/applications/${id}/certificates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setCertificates(data.certificates || []);
    } catch (error) {
      console.error('Error loading certificates:', error);
    } finally {
      setLoadingCertificates(false);
    }
  };

  const handleDownloadCertificatePDF = async (certId: number, certTitle: string) => {
    try {
      const token = localStorage.getItem('grant_crm_token');
      const response = await fetch(`/api/crm/certificates/${certId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await response.blob();
      downloadBlob(blob, `Certyfikat_${certTitle.replace(/\s+/g, '_')}_${certId}.pdf`);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error('❌ Błąd podczas pobierania certyfikatu');
    }
  };

  const handleDownloadAllCertificatesPDF = async () => {
    if (!id || !application) return;
    try {
      const token = localStorage.getItem('grant_crm_token');
      const response = await fetch(`/api/crm/applications/${id}/certificates/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await response.blob();
      downloadBlob(blob, `Wszystkie_Certyfikaty_${application.first_name}_${application.last_name}_${id}.pdf`);
      toast.info('✅ Pobrano wszystkie certyfikaty jako PDF');
    } catch (error) {
      console.error('Error downloading certificates bundle:', error);
      toast.error('❌ Błąd podczas pobierania pakietu certyfikatów');
    }
  };

  const handleGenerateCertificates = async (type?: string) => {
    if (!id) return;
    setGeneratingCertificates(true);
    try {
      const pkg = await crmApi.generateCertificates(parseInt(id), type);
      setCertificatePackage(pkg);
      setShowCertificates(true);
    } catch (error) {
      console.error('Error generating certificates:', error);
      toast.error('Błąd podczas generowania zaświadczeń');
    } finally {
      setGeneratingCertificates(false);
    }
  };

  const handleGenerateWithFormData = async (formData: CertificateFormData) => {
    if (!id) return;
    setShowCertificateForm(false);
    setGeneratingCertificates(true);
    try {
      const pkg = await crmApi.generateCertificates(parseInt(id), formData.certificateType, formData as unknown as Record<string, unknown>);
      setCertificatePackage(pkg);
      setShowCertificates(true);
    } catch (error) {
      console.error('Error generating certificates:', error);
      toast.error('Błąd podczas generowania zaświadczeń');
    } finally {
      setGeneratingCertificates(false);
    }
  };

  const handleExportToExcel = async () => {
    setSaving(true);
    try {
      const blob = await crmApi.exportToExcel();
      downloadBlob(blob, `Wnioski_PUP_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('✅ Eksport do Excel zakończony pomyślnie!');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('❌ Błąd podczas eksportu do Excel');
    } finally {
      setSaving(false);
    }
  };

  return {
    generatingPUPApplication, generatingPDF, sendingEmail,
    documentsRefreshTrigger, setDocumentsRefreshTrigger,
    certificates, showCertificateArchive, setShowCertificateArchive, loadingCertificates,
    certificatePackage, showCertificates, setShowCertificates,
    showCertificateForm, setShowCertificateForm, generatingCertificates,
    handleDownloadContract, handleDownloadDocuments, handleCalculatePoints,
    handleGeneratePUPApplication, handleGeneratePUPApplicationPDF,
    handleSendWelcomeEmail, handleSendDocumentPackage,
    loadCertificates, handleDownloadCertificatePDF, handleDownloadAllCertificatesPDF,
    handleGenerateCertificates, handleGenerateWithFormData,
    handleExportToExcel,
  };
}
